import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * NavItem — Atom
 * Single navigation item for the sidebar rail.
 * Keyboard navigable, supports active state and notification badge.
 *
 * The practitioner app's rail (Antonin, 12 Oct; one shell for both apps): folded, icon only, 20px in a 40px
 * tile (open: the label beside it) with `--radius-rail-item` corners. The label is the accessible name and, for a destination,
 * the rail's instant tooltip (SideNav); controls (bell) carry none. Inactive: secondary ink, panel
 * grey on hover. Active: the active-bg pill with the brand icon. Colour only on the active item.
 */

interface NavItemProps {
  icon: LucideIcon;
  label: string;
  isActive?: boolean;
  badge?: number;
  onClick?: () => void;
  /** A destination (a module of the app): the rail names it in its instant tooltip. */
  destination?: boolean;
  /** The open rail: the label sits beside the icon (the icon keeps its exact position). */
  expanded?: boolean;
}

export function NavItem({ icon: Icon, label, isActive = false, badge, onClick, destination = false, expanded = false }: NavItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // Anchored left in both rail states, so the icon never moves while the rail's width animates.
        'relative flex h-10 shrink-0 items-center gap-3 overflow-hidden rounded-[var(--radius-rail-item)] px-3 text-left transition-colors',
        expanded ? 'w-full' : 'w-10',
        isActive
          ? 'bg-[var(--accent-active-bg)] text-[var(--accent-primary)]'
          : 'text-[var(--ink-secondary)] hover:bg-[var(--surface-panel)] hover:text-[var(--ink-body)] focus-visible:bg-[var(--surface-panel)]'
      )}
      aria-current={isActive ? 'page' : undefined}
      aria-label={label}
      data-nav-destination={destination ? label : undefined}
    >
      <Icon className="size-5 shrink-0" aria-hidden />
      <span aria-hidden={!expanded} className="truncate whitespace-nowrap text-sm font-medium">
        {label}
      </span>
      {badge != null && badge > 0 && (
        <span
          className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--status-bad)] px-1 text-xs font-medium text-[var(--color-text-inverse)]"
          aria-label={`${badge} notifications`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
